/* eslint-disable max-len */
import { message } from 'antd';
import { Formik } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import { EmailContentUpdateType, type EventApplicationModuleInDealerSpecsFragment } from '../../../../api';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { useUpdateApplicationDealershipAssignmentsByDealerMutation } from '../../../../api/mutations/updateApplicationDealershipAssignmentsByDealer';
import {
    type UpdateEventApplicationModuleEmailContentsMutationVariables,
    useUpdateEventApplicationModuleEmailContentsMutation,
} from '../../../../api/mutations/updateEventApplicationModuleEmailContents';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import Form from '../../../../components/fields/Form';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';
import VehicleAssignmentsSection from '../../../shared/VehicleAssignmentsSection';
import EmailContentsForm from '../../ModuleDetailsPage/EventApplicationModulePage/EmailContentsForm';
import useHandleEventApplicationModuleAssets from '../../ModuleDetailsPage/EventApplicationModulePage/useHandleEventApplicationModuleAssets';

export type DealerAssignmentEventFormProps = {
    module: EventApplicationModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type FormValues = {
    dealerVehicleSuiteIds: string[];
    emailContents: EventApplicationModuleInDealerSpecsFragment['emailContents'];
};

const DealerAssignmentEventForm = ({ module, dealer }: DealerAssignmentEventFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'eventApplicationModuleDetails']);
    const [mutation] = useUpdateApplicationDealershipAssignmentsByDealerMutation();
    const [updateEventEmailMutation] = useUpdateEventApplicationModuleEmailContentsMutation();

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            dealerVehicleSuiteIds:
                module.dealerVehicles.find(({ dealerId }) => dealerId === dealer.id)?.vehicleSuiteIds || [],
            emailContents: module.emailContents,
        }),
        [dealer.id, module]
    );

    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: hasPermissions(dealer.permissions, [permissionKind.updateDealer]),
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [dealer.permissions, module.id]
    );

    const handleEventModuleAssets = useHandleEventApplicationModuleAssets();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { dealerVehicleSuiteIds, emailContents } = values;
            const { submitOrder } = emailContents;
            const { introImage: submitOrderIntroImage, ...submitOrderSetting } = submitOrder;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            const dealerVehicles = { dealerId: dealer.id, vehicleSuiteIds: dealerVehicleSuiteIds };
            const variables = {
                moduleId: module.id,
                dealerVehicles,
            };

            const updateEmailVariables: UpdateEventApplicationModuleEmailContentsMutationVariables = {
                moduleId: module.id,
                settings: {
                    emailContentUpdateType: EmailContentUpdateType.Dealer,
                    dealerId: dealer.id,
                    submitOrder: submitOrderSetting,
                },
            };

            await mutation({ variables })
                .then(() => updateEventEmailMutation({ variables: updateEmailVariables }))
                .finally(() => {
                    message.destroy('primary');
                });

            await handleEventModuleAssets(
                module.id,
                { submitOrder },
                EmailContentUpdateType.Dealer,
                module.emailContents,
                dealer.id
            );

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [t, dealer.id, module.id, module.emailContents, mutation, handleEventModuleAssets, updateEventEmailMutation]
    );

    const formId = `dealerForm-${module.id}`;
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
                {({ handleSubmit }) => (
                    <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                        <VehicleAssignmentsSection
                            disabled={!hasUpdatePermission}
                            filter={{ moduleId: module.vehicleModuleId }}
                            header={t('dealershipManagement:tabs.panel.vehicleAssignment')}
                            name="dealerVehicleSuiteIds"
                        />
                        <CollapsibleWrapper defaultActiveKey={['email']}>
                            <Panel key="email" header={t('eventApplicationModuleDetails:tabs.emailContents.title')}>
                                <EmailContentsForm
                                    disabled={!hasUpdatePermission}
                                    prefix="emailContents"
                                    showUpdateButton={false}
                                    targetDealerId={dealer.id}
                                    defaultValueDisabled
                                />
                            </Panel>
                        </CollapsibleWrapper>
                    </Form>
                )}
            </Formik>
        </GenericStateOnPageContextProvider>
    );
};

export default DealerAssignmentEventForm;
